import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { notificationPreferenceAPI } from '../../services/api'
import {
  HiOutlineBell,
  HiOutlineUserGroup,
  HiOutlineFolder,
  HiOutlineDocumentText,
  HiOutlineChatBubbleLeft,
  HiOutlineLink,
  HiOutlineCog6Tooth,
  HiOutlineCalendar,
  HiOutlineRectangleStack,
} from 'react-icons/hi2'
import {
  Banner,
  Button,
  Card,
  PageHeader,
  Spinner,
} from '../../components/ui'

// Translation key suffixes match settings.notifications.* — actual labels resolved at render time
const PREFERENCE_ITEMS = [
  { key: 'notify_client',         labelKey: 'client',         descKey: 'clientDesc',         icon: HiOutlineUserGroup,        tone: 'info' },
  { key: 'notify_case',           labelKey: 'case',           descKey: 'caseDesc',           icon: HiOutlineFolder,           tone: 'brand' },
  { key: 'notify_session',        labelKey: 'session',        descKey: 'sessionDesc',        icon: HiOutlineCalendar,         tone: 'success' },
  { key: 'notify_document',       labelKey: 'document',       descKey: 'documentDesc',       icon: HiOutlineDocumentText,     tone: 'warning' },
  { key: 'notify_document_group', labelKey: 'documentGroup',  descKey: 'documentGroupDesc',  icon: HiOutlineRectangleStack,   tone: 'info' },
  { key: 'notify_message',        labelKey: 'message',        descKey: 'messageDesc',        icon: HiOutlineChatBubbleLeft,   tone: 'brand' },
  { key: 'notify_connection',     labelKey: 'connection',     descKey: 'connectionDesc',     icon: HiOutlineLink,             tone: 'brand' },
  { key: 'notify_system',         labelKey: 'system',         descKey: 'systemDesc',         icon: HiOutlineCog6Tooth,        tone: 'neutral' },
]

const TONE_CHIP = {
  brand:   'bg-brand-soft text-brand',
  info:    'bg-info-soft text-info',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger:  'bg-danger-soft text-danger',
  neutral: 'bg-muted text-ink-secondary',
}

export default function NotificationPreferencesPage() {
  const { t } = useTranslation()
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const [preferences, setPreferences] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(null)

  const getParams = useCallback(() => {
    const params = {}
    if (isWorkspaceSwitched && ownerAccountId) params.owner_account_id = ownerAccountId
    return params
  }, [isWorkspaceSwitched, ownerAccountId])

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true)
      const response = await notificationPreferenceAPI.getPreferences(getParams())
      if (response.success) {
        setPreferences(response.data?.data || response.data)
      } else {
        showError(t('settings.notifications.loadFailed'))
      }
    } catch {
      showError(t('settings.notifications.loadFailed'))
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getParams])

  useEffect(() => {
    fetchPreferences()
  }, [fetchPreferences])

  const handleToggle = async (key) => {
    if (!preferences) return
    const newValue = !preferences[key]
    const oldPrefs = { ...preferences }
    setPreferences(prev => ({ ...prev, [key]: newValue }))
    setSaving(key)
    try {
      const response = await notificationPreferenceAPI.updatePreferences({ [key]: newValue }, getParams())
      if (response.success) {
        setPreferences(response.data?.data || response.data)
        const item = PREFERENCE_ITEMS.find(p => p.key === key)
        const label = item ? t(`settings.notifications.${item.labelKey}`) : key
        showSuccess(t(newValue ? 'settings.notifications.enabledSuccess' : 'settings.notifications.disabledSuccess', { label }))
      } else {
        setPreferences(oldPrefs)
        showError(t('settings.notifications.updateFailed'))
      }
    } catch {
      setPreferences(oldPrefs)
      showError(t('settings.notifications.updateFailed'))
    } finally {
      setSaving(null)
    }
  }

  const handleBulk = async (enable) => {
    const next = {}
    PREFERENCE_ITEMS.forEach(item => { next[item.key] = enable })
    setPreferences(prev => ({ ...prev, ...next }))
    setSaving('all')
    try {
      const response = await notificationPreferenceAPI.updatePreferences(next, getParams())
      if (response.success) {
        setPreferences(response.data?.data || response.data)
        showSuccess(t('settings.notifications.bulkSuccess', {
          action: enable ? t('common.active').toLowerCase() : t('common.inactive').toLowerCase(),
        }))
      }
    } catch {
      showError(t('settings.notifications.updateFailed'))
      fetchPreferences()
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="space-y-2">
          <div className="h-7 w-1/3 bg-muted rounded animate-pulse" />
          <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
        ))}
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  const enabledCount = preferences ? PREFERENCE_ITEMS.filter(item => preferences[item.key]).length : 0

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <PageHeader
        icon={HiOutlineBell}
        iconTone="brand"
        title={t('settings.notifications.title')}
        subtitle={
          isWorkspaceSwitched
            ? t('settings.notifications.subtitleSwitched')
            : t('settings.notifications.subtitle')
        }
      />

      <Card padding="md">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-ink-secondary">
            {t('settings.notifications.summary', { enabled: enabledCount, total: PREFERENCE_ITEMS.length })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleBulk(true)}
              disabled={saving === 'all'}
              className="!text-success hover:!bg-success-soft hover:!border-success/40"
            >
              {t('settings.notifications.enableAll')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleBulk(false)}
              disabled={saving === 'all'}
              className="!text-danger hover:!bg-danger-soft hover:!border-danger/40"
            >
              {t('settings.notifications.disableAll')}
            </Button>
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        {PREFERENCE_ITEMS.map((item) => {
          const Icon = item.icon
          const isEnabled = preferences?.[item.key] ?? true
          const isSaving = saving === item.key
          return (
            <Card key={item.key} padding="md" className={isEnabled ? '' : 'opacity-75'}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${TONE_CHIP[item.tone] || TONE_CHIP.brand}`}>
                    <Icon className="w-5 h-5" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-ink-primary">{t(`settings.notifications.${item.labelKey}`)}</h3>
                    <p className="text-xs text-ink-muted mt-0.5">{t(`settings.notifications.${item.descKey}`)}</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleToggle(item.key)}
                  disabled={isSaving}
                  role="switch"
                  aria-checked={isEnabled}
                  aria-label={t(`settings.notifications.${item.labelKey}`)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${
                    isEnabled ? 'bg-brand' : 'bg-border-strong'
                  } ${isSaving ? 'opacity-50' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                      isEnabled ? 'translate-x-6 rtl:-translate-x-6' : 'translate-x-1 rtl:-translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </Card>
          )
        })}
      </div>

      <Banner tone="info" title={t('settings.notifications.guideTitle')}>
        <ul className="list-disc list-inside ms-1 space-y-1 text-sm">
          <li>{t('settings.notifications.guide1')}</li>
          <li>{t('settings.notifications.guide2')}</li>
          <li>{t('settings.notifications.guide3')}</li>
          <li>{t('settings.notifications.guide4')}</li>
        </ul>
      </Banner>
    </div>
  )
}
